import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { NoAccess } from "@/components/NoAccess";
import { HeroProgress, PlanHero } from "@/components/kit/PlanHero";
import { ProgressRing } from "@/components/kit/ProgressRing";
import { WeekIndex } from "@/components/kit/WeekIndex";
import { api, routes } from "@/lib/ppcp";
import { useProgress } from "@/lib/progress";
import { PAGES } from "@/constants/testIds";

export default function DaysIndex() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [pack, setPack] = useState(null);
  const [denied, setDenied] = useState(false);
  const { summary } = useProgress();

  useEffect(() => {
    api
      .get(`/programs/${slug}`)
      .then((res) => setPack(res.data))
      .catch((err) => (err.response?.status === 403 ? setDenied(true) : setPack(false)));
  }, [slug]);

  if (denied) return <NoAccess title="The Full Plan" />;

  const total = pack?.program?.duration_days ?? 0;
  const authored = new Set((pack?.days ?? []).map((d) => d.day_number));
  const progress = summary ?? pack?.progress;
  const completed = progress?.completed_days ?? [];
  const done = progress?.days_complete ?? 0;

  return (
    <AppShell
      title="The Full Plan"
      subtitle={pack?.program?.title}
      slug={slug}
      size="reading"
      crumbs={[
        { label: "My Plans", to: routes.library },
        { label: pack?.program?.title ?? "Program", to: routes.program(slug) },
        { label: "Day Index" },
      ]}
    >
      <div data-testid={PAGES.days} className="contents">
        <PlanHero
          eyebrow={`${total} days · ${pack?.week_count ?? 0} weeks`}
          title={progress?.started ? `Day ${progress.resume_day} is next` : "All days are unlocked"}
          lead={
            progress?.finished
              ? "You've finished every day. Revisit any day, or start again from Day 1."
              : "Move freely between days — your progress is kept per day. Faded days have no content yet."
          }
          aside={
            <ProgressRing value={total ? (done / total) * 100 : 0} size={104} tone="dark" />
          }
          footer={
            <HeroProgress done={done} total={total} percent={progress?.percent ?? 0} testId="days-progress-line" />
          }
        />

        <section className="rounded-[22px] bg-white p-5 shadow-card sm:p-6">
          <header className="mb-4 flex items-baseline justify-between gap-3">
            <h2 className="ps-display text-[19px] font-bold sm:text-[21px]">Every day, grouped by week</h2>
            <span className="whitespace-nowrap text-[12.5px] font-medium text-muted-foreground">
              Tap any day to open it
            </span>
          </header>
          <WeekIndex
            total={total}
            current={progress?.resume_day ?? 1}
            completed={completed}
            authored={authored}
            days={pack?.days ?? []}
            onSelect={(n) => navigate(routes.day(slug, n))}
          />
        </section>
      </div>
    </AppShell>
  );
}
